import React from 'react';
import lodash, { values } from 'lodash';

import { TextField, CircularProgress, Autocomplete, Chip } from '@mui/material';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';

import { returnLoadingText } from 'src/utils/functions';
import { COLOR_PRIMARY_LIGHT, COLOR_PRIMARY } from 'src/const';
import { SearchbarAutocompletePropTypes } from 'src/interfaces/iproptypes';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    options: {
      '&:hover': {
        backgroundColor: COLOR_PRIMARY_LIGHT,
        fontColor: '#abcdef',
        textColor: '#abcdef',
      },
    },
  })
);

const SearchbarAutocomplete = ({
  id,
  multipleSearch,
  multipleSearchLimit,
  shrink,
  open,
  setOpen,
  loading,
  options,
  defaultValue,
  value,
  label,
  handleInputChange,
  handleSearchChange,
  endAdorement,
  isEmptySearchbar,
  inputElementStyle,
  inputLabelStyle,
  handleOnMouseEnter,
  handleOnMouseLeave,
}: SearchbarAutocompletePropTypes) => {
  const classes = useStyles();

  return (
    <div>
      <Autocomplete
        multiple={multipleSearch}
        id={id}
        open={open}
        classes={{
          option: classes.options,
        }}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false);
        }}
        options={options}
        filterOptions={(options, params) => options.filter(option => !value.includes(option))}
        getOptionDisabled={options =>
          multipleSearch && multipleSearchLimit && value.length >= multipleSearchLimit
            ? true
            : false
        }
        loading={loading}
        onInputChange={lodash.debounce((value, event) => handleInputChange(value, event), 600)} // this one being called whenever user inputs a letter
        onChange={(_, newValue) => handleSearchChange(newValue)} // this one being called after user click the value in the dropdown
        noOptionsText={'No search result'}
        loadingText={returnLoadingText(isEmptySearchbar)}
        defaultValue={defaultValue}
        value={value}
        renderTags={(value: readonly string[], getTagProps) =>
          value.map((option: string, index: number) => (
            <Chip
              variant="outlined"
              label={option}
              {...getTagProps({ index })}
              sx={{
                fontSize: inputElementStyle.fontSize,
                fontWeight: inputElementStyle.fontWeight,
                background: inputElementStyle.chipBackgroundColor,
              }}
            />
          ))
        }
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            InputLabelProps={{
              shrink: shrink,
              sx: {
                color: inputLabelStyle.color,
                '&.Mui-focused': {
                  color: inputLabelStyle.colorFocused,
                  fontWeight: inputLabelStyle.fontWeight,
                },
              },
            }}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              sx: {
                backgroundColor: inputElementStyle.backgroundColor,
                borderRadius: inputElementStyle.borderRadius,
                '& .MuiOutlinedInput-notchedOutline': {
                  border: `${inputElementStyle.border}!important`,
                },
                '&:hover fieldset': {
                  border: `${inputElementStyle.borderFocused}!important`,
                },
                '&:focus-within fieldset, &:focus-visible fieldset': {
                  border: `${inputElementStyle.borderFocused}!important`,
                },
              },
              endAdornment: (
                <>
                  {!isEmptySearchbar && loading ? (
                    <CircularProgress color="primary" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                  {endAdorement}
                </>
              ),
            }}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
        )}
      />
    </div>
  );
};

export default SearchbarAutocomplete;
